/**
 * Route constants — single source of truth so screens, redirects, and
 * the React Router config all agree on the URL shape.
 */
import { createContext, useContext } from "react";
export const RecordsModeContext = createContext(false);
export const useRecordsMode = () => useContext(RecordsModeContext);
export function recordsRouteAllowed(path: string): boolean {
  if (path === "/compliance" || path.startsWith("/compliance/") || path.startsWith("/ack/") || path.endsWith("/compliance")) return true;
  return /^(?:\/$|\/landing$|\/sign(?:in|up|out)$|\/home(?:\/empty)?$|\/clients(?:\/[^/]+)?$|\/inbox$|\/subscribe$|\/plan$|\/billing\/success$|\/admin$|\/onboarding\/)/.test(path);
}
export const Routes = {
  // Public / pre-auth
  SignUp: "/signup",
  SignIn: "/signin",

  // Post-auth, pre-activation
  IFAVerify: "/onboarding/ifa-verify",
  Pending: "/onboarding/pending",

  // IFA workspace (auth + active + role=ifa)
  IFAHome: "/clients",
  IFAEmpty: "/clients/empty",
  AddClient: "/clients/new",
  ClientDetail: "/clients/:clientId",
  ClientCompliance: "/clients/:clientId/compliance",

  // Investment Adviser compliance desk (IFA + SEBI registration; server-gated)
  Compliance: "/compliance",

  // Public: the client's acknowledgement page. No account, token is the credential.
  ClientAck: "/ack/:token",

  // Individual workspace (auth + active + role=individual)
  IndividualHome: "/home",
  IndividualEmpty: "/home/empty",

  // Fund research workspace (auth + active; MFD + IFA — discover/compare/AI note)
  Funds: "/funds",

  // Statement inbox — the private forwarding address that keeps a book current
  // without any account linking. Cross-role: an advisor forwards their own
  // statements the same way a retail user does.
  Inbox: "/inbox",

  // Cross-role surfaces (auth + active; either role)
  Alerts: "/alerts",
  Watchlist: "/watchlist",
  Library: "/library",
  LibraryItem: "/library/:id",
  Reports: "/reports",
  Practice: "/practice",
  Market: "/market",

  // Prospect proposal — advisor-only client-acquisition surface (IFA + MFD)
  Prospect: "/prospect",

  // Briefing surfaces (auth + active; either role)
  BriefingProgress: "/brief/:briefingId/progress",
  BriefingViewer: "/brief/:briefingId",

  // Subscription (legacy v1 mock — kept for the route during migration)
  Subscribe: "/subscribe",

  // Billing — credits / packs (v2 pivot from subscription)
  BillingPacks: "/billing/packs",
  BillingSuccess: "/billing/success",

  // NO `Settings` KEY. Removed 2026-09-05 (F36): it was declared here as
  // "/settings", referenced by nothing, and had no <Route>. A route constant
  // that resolves to a 404 is worse than a missing one — it invites a link.
  // Re-add it in the same commit that adds the screen and the <Route>.
} as const;

export type RouteKey = keyof typeof Routes;

/** Workspace home for a persona. IFA + MFD both land on the client book
 *  (/clients); retail lands on their personal portfolio (/home). */
export function homeFor(role?: string): string {
  return role === "individual" ? Routes.IndividualHome : Routes.IFAHome;
}

/** Capability helpers — single source of truth for persona gating in the UI. */
export const canEquityAI = (role?: string): boolean => role === "ifa" || role === "ra";
export const canMfAI = (role?: string): boolean => role === "ifa" || role === "mfd" || role === "ra";
export const isAdvisor = (role?: string): boolean => role === "ifa" || role === "mfd" || role === "ra";
/** The full research workspace SHAPE — equity brief archive + track record,
 *  daily picks, the equity sleeve on Prospect, and the clients-workspace nav
 *  scope. IFA (the server keeps a records-mode IFA out of research anyway)
 *  and the Research Analyst role, which was carved out of the IFA workspace
 *  in 2026-09 and must inherit every `role === "ifa"` site that meant "the
 *  research adviser", not "Investment Adviser compliance" (that stays IFA).
 *  Distinct from isAdvisor: an MFD has clients but no equity research. */
export const hasResearchWorkspace = (role?: string): boolean => role === "ifa" || role === "ra";
/** AppNav `scope` for a persona: "ifa" is the clients workspace. */
export const navScopeFor = (role?: string): "ifa" | "individual" =>
  hasResearchWorkspace(role) ? "ifa" : "individual";
